import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { Activity, Zap, TrendingUp, AlertTriangle, Clock, ChevronUp, ChevronDown } from 'lucide-react';

interface MobilityMetrics {
  timestamp: string;
  walkingSpeed: number;
  strideLength: number;
  stepFrequency: number;
  stepSymmetry: number;
  balanceScore: number;
  turnDuration: number;
  dualTaskPerformance: number;
}

interface CognitiveIndicators {
  timestamp: string;
  spatialNavigation: number;
  movementPlanning: number;
  dualTaskScore: number;
  patternDeviation: number;
  adaptability: number;
}

interface RiskAssessment {
  category: string;
  score: number;
  trend: number;
  threshold: number;
  weight: number;
}

const MobilityHealth: React.FC = () => {
  // Mock data - in real app this would come from floor sensors and ML models
  const mobilityData: MobilityMetrics[] = [
    { timestamp: '6AM', walkingSpeed: 2.6, strideLength: 0.55, stepFrequency: 1.8, stepSymmetry: 92, balanceScore: 88, turnDuration: 2.8, dualTaskPerformance: 85 },
    { timestamp: '9AM', walkingSpeed: 2.8, strideLength: 0.58, stepFrequency: 1.9, stepSymmetry: 94, balanceScore: 90, turnDuration: 2.6, dualTaskPerformance: 88 },
    { timestamp: '12PM', walkingSpeed: 2.7, strideLength: 0.56, stepFrequency: 1.85, stepSymmetry: 93, balanceScore: 89, turnDuration: 2.7, dualTaskPerformance: 86 },
    { timestamp: '3PM', walkingSpeed: 2.5, strideLength: 0.54, stepFrequency: 1.75, stepSymmetry: 91, balanceScore: 87, turnDuration: 2.9, dualTaskPerformance: 83 },
    { timestamp: '6PM', walkingSpeed: 2.4, strideLength: 0.52, stepFrequency: 1.7, stepSymmetry: 90, balanceScore: 85, turnDuration: 3.0, dualTaskPerformance: 80 }
  ];

  const cognitiveData: CognitiveIndicators[] = [
    { timestamp: '6AM', spatialNavigation: 88, movementPlanning: 90, dualTaskScore: 85, patternDeviation: 5, adaptability: 87 },
    { timestamp: '9AM', spatialNavigation: 90, movementPlanning: 92, dualTaskScore: 88, patternDeviation: 4, adaptability: 89 },
    { timestamp: '12PM', spatialNavigation: 89, movementPlanning: 91, dualTaskScore: 86, patternDeviation: 6, adaptability: 88 },
    { timestamp: '3PM', spatialNavigation: 87, movementPlanning: 89, dualTaskScore: 83, patternDeviation: 7, adaptability: 86 },
    { timestamp: '6PM', spatialNavigation: 85, movementPlanning: 87, dualTaskScore: 80, patternDeviation: 8, adaptability: 84 }
  ];

  const riskAssessments: RiskAssessment[] = [
    { category: 'Fall Risk', score: 82, trend: -2, threshold: 80, weight: 0.3 },
    { category: 'Balance Stability', score: 85, trend: 1, threshold: 75, weight: 0.2 },
    { category: 'Gait Rhythm', score: 88, trend: 2, threshold: 85, weight: 0.15 },
    { category: 'Cognitive Function', score: 86, trend: -1, threshold: 80, weight: 0.2 },
    { category: 'Movement Planning', score: 84, trend: 0, threshold: 75, weight: 0.15 }
  ];

  const radarData = [
    {
      metric: 'Walking Speed',
      value: mobilityData[mobilityData.length - 1].walkingSpeed * 100,
      fullMark: 100,
    },
    {
      metric: 'Step Symmetry',
      value: mobilityData[mobilityData.length - 1].stepSymmetry,
      fullMark: 100,
    },
    {
      metric: 'Balance',
      value: mobilityData[mobilityData.length - 1].balanceScore,
      fullMark: 100,
    },
    {
      metric: 'Dual Task',
      value: mobilityData[mobilityData.length - 1].dualTaskPerformance,
      fullMark: 100,
    },
    {
      metric: 'Turn Control',
      value: (3.5 - mobilityData[mobilityData.length - 1].turnDuration) * 20,
      fullMark: 100,
    },
  ];

  const getScoreColor = (score: number, threshold: number) => {
    if (score >= threshold + 10) return 'text-green-500';
    if (score >= threshold) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <ChevronUp className="text-green-500" />;
    if (trend < 0) return <ChevronDown className="text-red-500" />;
    return <span className="text-gray-500">→</span>;
  };

  const calculateOverallScore = (assessments: RiskAssessment[]) => {
    return Math.round(
      assessments.reduce((acc, curr) => acc + curr.score * curr.weight, 0)
    );
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Mobility Health Analysis</h2>
      
      {/* Overall Scores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Overall Mobility Score</h3>
          </div>
          <p className="text-3xl font-bold">{calculateOverallScore(riskAssessments)}</p>
          <p className="text-sm text-gray-500">Based on multiple factors</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Zap className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Cognitive Index</h3>
          </div>
          <div className="flex items-center">
            <p className="text-3xl font-bold">{riskAssessments.find(r => r.category === 'Cognitive Function')?.score}</p>
            {getTrendIcon(riskAssessments.find(r => r.category === 'Cognitive Function')?.trend || 0)}
          </div>
          <p className="text-sm text-gray-500">Movement-based assessment</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Fall Risk Level</h3>
          </div>
          <div className="flex items-center">
            <p className="text-3xl font-bold">{riskAssessments.find(r => r.category === 'Fall Risk')?.score}</p>
            {getTrendIcon(riskAssessments.find(r => r.category === 'Fall Risk')?.trend || 0)}
          </div>
          <p className="text-sm text-gray-500">Composite risk score</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Zap className="text-yellow-500 mr-2" />
            <h3 className="text-lg font-semibold">Movement Quality</h3>
          </div>
          <div className="flex items-center">
            <p className="text-3xl font-bold">{riskAssessments.find(r => r.category === 'Gait Rhythm')?.score}</p>
            {getTrendIcon(riskAssessments.find(r => r.category === 'Gait Rhythm')?.trend || 0)}
          </div>
          <p className="text-sm text-gray-500">Overall movement assessment</p>
        </div>
      </div>

      {/* Detailed Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Mobility Metrics Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mobilityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="walkingSpeed" 
                name="Walking Speed (ft/s)"
                stroke="#3b82f6"
                strokeWidth={2}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="stepFrequency" 
                name="Step Frequency (steps/s)"
                stroke="#8b5cf6"
                strokeWidth={2}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="stepSymmetry" 
                name="Step Symmetry (%)"
                stroke="#10b981"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Movement Pattern Analysis</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart outerRadius={90} data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metric" />
              <PolarRadiusAxis angle={30} domain={[0, 100]} />
              <Radar
                name="Current Performance"
                dataKey="value"
                stroke="#8884d8"
                fill="#8884d8"
                fillOpacity={0.6}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cognitive Indicators */}
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Cognitive Health Indicators</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cognitiveData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="spatialNavigation" 
                name="Spatial Navigation"
                stroke="#3b82f6"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="movementPlanning" 
                name="Movement Planning"
                stroke="#8b5cf6"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="dualTaskScore" 
                name="Dual Task Performance"
                stroke="#10b981"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="patternDeviation" 
                name="Pattern Deviation"
                stroke="#f59e0b"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Risk Assessment Details */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Detailed Risk Assessment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {riskAssessments.map((assessment, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-medium">{assessment.category}</span>
                <div className="flex items-center space-x-2">
                  <span className={getScoreColor(assessment.score, assessment.threshold)}>
                    {assessment.score}
                  </span>
                  {getTrendIcon(assessment.trend)}
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    assessment.score >= assessment.threshold + 10
                      ? 'bg-green-500'
                      : assessment.score >= assessment.threshold
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${assessment.score}%` }}
                />
              </div>
              <p className="text-sm text-gray-500">
                Threshold: {assessment.threshold} | Weight: {(assessment.weight * 100).toFixed(0)}%
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Insights and Recommendations */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Analysis Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Activity className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Movement Patterns</h4>
              <p className="text-gray-600">
                Walking speed and step symmetry show strong correlation with overall mobility health.
                Current patterns indicate stable gait with minor afternoon decline.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Zap className="text-purple-500" />
            </div>
            <div>
              <h4 className="font-medium">Cognitive Function Indicators</h4>
              <p className="text-gray-600">
                Spatial navigation and dual-task performance metrics suggest stable cognitive function.
                Minor variations in pattern recognition warrant continued monitoring.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-orange-500" />
            </div>
            <div>
              <h4 className="font-medium">Risk Assessment</h4>
              <p className="text-gray-600">
                Overall fall risk remains low with good balance scores. 
                Recommended focus on maintaining current activity levels and continuing prescribed exercises.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilityHealth; 